import { createSvgEl } from '@learnforge/demo-render';

export interface CurveOptions {
  fn: (x: number) => number;
  xDomain: [number, number];
  samples?: number;
  className?: string;
  clipAtY?: [number, number];
}

export interface CurveView {
  xDomain: [number, number];
  samples?: number;
  clipAtY?: [number, number];
}

export interface CurveHandle {
  element: SVGPathElement;
  setFn(fn: (x: number) => number): void;
  setDomain(d: [number, number]): void;
  /** Change domain, sample count, and/or clip band in a single rebuild. */
  setView(v: CurveView): void;
  destroy(): void;
}

function buildPath(opts: CurveOptions): string {
  const samples = opts.samples ?? 240;
  const [x0, x1] = opts.xDomain;
  const step = (x1 - x0) / (samples - 1);
  const clip = opts.clipAtY;
  let d = '';
  let inSegment = false;
  for (let i = 0; i < samples; i++) {
    const x = x0 + i * step;
    const y = opts.fn(x);
    const ok = Number.isFinite(y) && (!clip || (y >= clip[0] && y <= clip[1]));
    if (!ok) {
      inSegment = false;
      continue;
    }
    if (!inSegment) {
      d += `M ${x.toFixed(4)} ${y.toFixed(4)} `;
      inSegment = true;
    } else {
      d += `L ${x.toFixed(4)} ${y.toFixed(4)} `;
    }
  }
  return d.trim();
}

export function createCurve(parent: SVGGElement, opts: CurveOptions): CurveHandle {
  let current: CurveOptions = { ...opts };
  const el = createSvgEl('path', {
    class: ['tg-curve', current.className ?? ''].filter(Boolean).join(' '),
    d: buildPath(current),
    'vector-effect': 'non-scaling-stroke',
  });
  parent.appendChild(el);

  return {
    element: el,
    setFn(fn) {
      current = { ...current, fn };
      el.setAttribute('d', buildPath(current));
    },
    setDomain(d) {
      current = { ...current, xDomain: d };
      el.setAttribute('d', buildPath(current));
    },
    setView(v) {
      current = {
        ...current,
        xDomain: v.xDomain,
        ...(v.samples !== undefined ? { samples: v.samples } : {}),
        ...(v.clipAtY !== undefined ? { clipAtY: v.clipAtY } : {}),
      };
      el.setAttribute('d', buildPath(current));
    },
    destroy() {
      el.remove();
    },
  };
}
